import React, { useEffect, useRef, useState } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import Icon from "react-native-vector-icons/Ionicons";
import { FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { PlaceColors } from "../helpers/PlaceColors";
import { PlaceText } from "./PlaceText";

const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
}

const getDefaultTitle = (language?: string) => {
    if (language?.startsWith("en")) return "Audio Guide";
    if (language?.startsWith("fr")) return "Audioguide";
    return "Audioguia";
}

export const SimpleAudioPlayer = ({
    audioUrl,
    findifyDataContext,
    title,
}: {
    audioUrl?: string,
    findifyDataContext: FindifyDataContextType | null,
    title?: string,
}) => {
    const colors = PlaceColors(findifyDataContext);
    const audioRef = useRef<any>(null);
    const [playing, setPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    useEffect(() => {
        setPlaying(false);
        setDuration(0);
        setCurrentTime(0);
    }, [audioUrl]);

    if (!audioUrl || Platform.OS !== "web") return null;

    const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;

    const toggleAudio = async () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (playing) {
            audio.pause();
            setPlaying(false);
            return;
        }

        await audio.play();
        setPlaying(true);
    }

    return <View style={[styles.card, { backgroundColor: colors.background.solidAlphaWhiteBase(75), borderColor: colors.primary.withAlpha(30) }]}>
        {React.createElement("audio", {
            ref: audioRef,
            src: audioUrl,
            preload: "metadata",
            onLoadedMetadata: (event: any) => setDuration(event.currentTarget.duration || 0),
            onTimeUpdate: (event: any) => setCurrentTime(event.currentTarget.currentTime || 0),
            onEnded: () => setPlaying(false),
        })}
        <Pressable style={[styles.playButton, { backgroundColor: colors.primary.get() }]} onPress={toggleAudio}>
            <Icon name={playing ? "pause" : "play"} size={24} color="#fff" />
        </Pressable>
        <View style={styles.content}>
            <View style={styles.titleRow}>
                <PlaceText style={[styles.title, { color: colors.primary.get() }]}>{title ?? getDefaultTitle(findifyDataContext?.language)}</PlaceText>
                <PlaceText style={[styles.time, { color: colors.text.withAlpha(170) }]}>{formatTime(currentTime)} / {formatTime(duration)}</PlaceText>
            </View>
            <View style={[styles.track, { backgroundColor: colors.primary.withAlpha(28) }]}>
                <View style={[styles.progress, { width: `${progress}%` as any, backgroundColor: colors.primary.get() }]} />
            </View>
        </View>
    </View>
}

const styles = StyleSheet.create({
    card: {
        borderWidth: 1,
        borderRadius: 8,
        padding: 12,
        marginBottom: 18,
        flexDirection: "row",
        gap: 12,
        alignItems: "center",
    },
    playButton: {
        width: 48,
        height: 48,
        borderRadius: 24,
        alignItems: "center",
        justifyContent: "center",
    },
    content: {
        flex: 1,
        minWidth: 0,
    },
    titleRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        marginBottom: 8,
    },
    title: {
        fontFamily: "Georgia",
        fontSize: 20,
        lineHeight: 24,
        fontWeight: "700",
    },
    time: {
        fontSize: 12,
        lineHeight: 16,
        fontWeight: "600",
    },
    track: {
        height: 5,
        borderRadius: 999,
        overflow: "hidden",
    },
    progress: {
        height: 5,
        borderRadius: 999,
    },
});
